import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Box, keyframes, useColorMode } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import environment, { electronOnly } from "src/utils/environment";
import { deviceMarks } from "src/modules/device/device-spec";
import { usePurchaseContext } from "src/modules/purchase/PurchaseContext";
import { isFreemium } from "src/modules/auth/duck/selectors";
import { getAvatars } from "src/modules/avatar/duck/selectors";
import CorrespondingAvatar from "src/modules/avatar/components/CorrespondingAvatar";
import stationSwitchProps from "src/modules/parent-station/lib/StationSwitchProps";
import { DeviceTabMark } from "src/modules/device/components/DeviceSpec";
import { Activities } from "src/modules/activity-log/activity";
import RoundButton from "src/components/RoundButton";
import { Subtitle2 } from "src/atoms/typography";
import { ErrorIndicator } from "./ErrorIndicator";
import { ReactComponent as AddStationIcon } from "./icons/add-station.svg";
import { ReactComponent as ToggleTilesIcon } from "./icons/toggle-tiles.svg";
import { ReactComponent as CloseIcon } from "./icons/close.svg";

const remote = window.require("@electron/remote");

const ActivityColors = {
  [Activities.AWAKE]: "warningBase",
  [Activities.MOTION]: "primary"
};

export default function Stations({
  toggleTilesView,
  connectedStations = {},
  babyStationSelect,
  renderTilesToggle,
  handleStationSwitch,
  renderStationPickerToggle,
  multiBabyManager,
  onTilesViewToggle,
  focusedStation,
  stopMonitoring,
  mode
}) {
  const { colorMode } = useColorMode();
  const isDarkMode = colorMode === "dark";

  const browserWindow = remote?.getCurrentWindow?.();
  const fullScreen = browserWindow?.isFullScreen();
  const { t } = useTranslation();

  const [frameStyles, setFrameStyles] = useState();
  const focusedStationIndex = useRef();
  const stationReferences = useRef([]);

  const [isFullScreen, setIsFullScreen] = useState(fullScreen);

  const isFreeVersion = useSelector(isFreemium);
  const avatars = useSelector(getAvatars);
  const purchase = usePurchaseContext();
  const stationsArray = Object.values(connectedStations);
  const hasOnlyOneStation = stationsArray.length === 1;

  useEffect(() => {
    electronOnly(() => {
      browserWindow.on("enter-full-screen", handleFullScreenChange);
      browserWindow.on("leave-full-screen", handleFullScreenChange);
    });
    return () => {
      electronOnly(() => {
        browserWindow.off("enter-full-screen", handleFullScreenChange);
        browserWindow.off("leave-full-screen", handleFullScreenChange);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserWindow]);

  function handleFullScreenChange() {
    const fullScreen = browserWindow.isFullScreen();
    setIsFullScreen(fullScreen);
  }

  useEffect(() => {
    function getFrameStyles() {
      const stylesRef = {
        x: environment.isMacOs ? (isFullScreen ? 129 : 195) : 105,
        width: 0
      };
      let foundFocused = false;
      Object.values(stationReferences.current).forEach((ref, i) => {
        if (!ref || foundFocused) return;
        if (i + 1 > stationsArray.length) {
          stationReferences.current[i + 1] = null;
          return;
        }
        if (i === focusedStationIndex.current) {
          stylesRef.width += ref.offsetWidth - 10;
          foundFocused = true;
          return;
        } else {
          stylesRef.x += ref.offsetWidth;
        }
      });
      setFrameStyles(stylesRef);
    }
    if (focusedStation.mode === "video") getFrameStyles();
  }, [focusedStation, stationsArray.length, isFullScreen]);

  useEffect(() => {
    if (frameStyles) {
      stationSwitchProps.setProps({ frameStyles });
    }
  }, [frameStyles]);

  useEffect(() => {
    return () => stationSwitchProps.resetProps();
  }, []);

  return (
    <Flex
      height={(hasOnlyOneStation ? 34 : 39) + "px"}
      position="fixed"
      top="0px"
      right="90px"
      left={environment.isMacOs ? (isFullScreen ? "128px" : "194px") : "104px"}
      zIndex={100}
    >
      {stationsArray &&
        stationsArray.length > 1 &&
        focusedStation &&
        stationsArray.map((station, i) => {
          const isFocusedStation = station.jid === focusedStation.jid;
          if (isFocusedStation) focusedStationIndex.current = i;
          const hasError = Boolean(station.error);

          const animate =
            (!isFocusedStation && station.activity !== Activities.SLEEPING) ||
            (!isFocusedStation && hasError);

          const alert = keyframes`
                0%, 27% {
                  background-color: transparent;
                  color: var(--chakra-colors-${
                    mode === "video" ? "white40" : "lowEmphasis"
                  });
                };
                40%, 85% {
                  background-color: var(--chakra-colors-${
                    ActivityColors[
                      hasError ? Activities.AWAKE : station.activity
                    ]
                  });
                  color: white;
                }
                100% {
                background-color: transparent;
                color: var(--chakra-colors-${
                  mode === "video" ? "white40" : "lowEmphasis"
                });
                }
                `;

          // here we are setting color, which has no effect on the actual style
          // it's there so we can reference "stations" & "hasError", we could use
          // anything else instead of color, that wouldn't break our styling.
          // We need to reference "stations" & "hasError" since it's referenced in
          // "alert" animation above and when one of those props changes, both animations
          // will rerender and remain in sync
          const textAnim = keyframes`
                0%, 27% {
                fill: var(--chakra-colors-${
                  mode === "video" ? "white40" : "lowEmphasis"
                });
                };
                40%, 85% {
                fill: white;
                color: var(--chakra-colors-${
                  ActivityColors[hasError ? Activities.AWAKE : station.activity]
                });
                }
                100% {
                fill: var(--chakra-colors-${
                  mode === "video" ? "white40" : "lowEmphasis"
                });
                }
                `;

          if (station.status === "disconnecting" && !isFocusedStation) {
            return null;
          }
          return (
            <Flex
              ref={(el) => (stationReferences.current[i] = el)}
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                handleStationSwitch(station);
              }}
              position="relative"
              alignItems="flex-end"
              maxWidth={`${
                Math.round((100 / stationsArray.length) * 10) / 10
              }%`}
            >
              {hasError && !isFocusedStation && <ErrorIndicator />}
              <Flex
                mr="8px"
                px="4px"
                pb="2px"
                pt="3px"
                alignItems="center"
                bg={
                  isFocusedStation &&
                  station.activity !== Activities.SLEEPING &&
                  ActivityColors[hasError ? Activities.AWAKE : station.activity]
                }
                border="1px solid"
                borderColor="iconBackground"
                borderTopLeftRadius="10px"
                borderTopRightRadius="10px"
                {...(isFocusedStation && {
                  borderBottomColor:
                    station.mode === "video"
                      ? "transparent"
                      : station.activity === Activities.SLEEPING
                      ? "surface"
                      : ActivityColors[
                          hasError ? Activities.AWAKE : station.activity
                        ],
                  borderColor:
                    station.mode === "video"
                      ? "transparent"
                      : station.activity === Activities.SLEEPING
                      ? "iconBackground"
                      : ActivityColors[
                          hasError ? Activities.AWAKE : station.activity
                        ]
                })}
                {...(!isFocusedStation && {
                  _hover: {
                    color: "highEmphasis",
                    bg: isDarkMode
                      ? `rgba(255, 255, 255, 0.08)`
                      : station.error ||
                        station.activity === Activities.AWAKE ||
                        station.activity === Activities.MOTION
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(143, 109, 245, 0.08)",
                    ...(station.mode === "video" && {
                      filter: "brightness(1.2)"
                    })
                  }
                })}
                {...(animate && {
                  animation: `2.2s ${alert} linear infinite`,
                  sx: {
                    "#close-anim-selector": {
                      animation: `2.2s ${textAnim} linear infinite`
                    }
                  }
                })}
              >
                <Box position="relative" minWidth="24px">
                  {station.isMultiRoom && (
                    <DeviceTabMark
                      deviceJid={station.jid}
                      mark={deviceMarks[i]}
                    />
                  )}
                  <CorrespondingAvatar
                    width="24px"
                    height="24px"
                    avatar={avatars[station.avatarId]}
                  />
                </Box>

                <Subtitle2
                  ml="6px"
                  overflowX="hidden"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  color={
                    animate
                      ? "inherit"
                      : station.activity !== Activities.SLEEPING
                      ? "white"
                      : isFocusedStation
                      ? mode === "video"
                        ? "white"
                        : "highEmphasis"
                      : mode === "video"
                      ? "white40"
                      : "lowEmphasis"
                  }
                >
                  {avatars[station.avatarId]?.name || t("avatar.myAvatar")}
                </Subtitle2>
                <Box mt="2px" ml="1px" minWidth="16px">
                  <RoundButton
                    iconFill={
                      isFocusedStation &&
                      station.activity !== Activities.SLEEPING
                        ? "white"
                        : mode === "video"
                        ? "white40"
                        : "lowEmphasis"
                    }
                    icon={CloseIcon}
                    size="16px"
                    iconSize="12px"
                    mr={station.isMultiRoom ? "12px" : "0px"}
                    onClick={(e) => {
                      e.stopPropagation();
                      stopMonitoring(station.jid);
                    }}
                  />
                </Box>
              </Flex>
            </Flex>
          );
        })}
      {renderStationPickerToggle && (
        <Flex alignItems="center" mt="4px" minWidth="24px">
          <RoundButton
            mr="12px"
            iconFill={
              mode === "video"
                ? "white"
                : hasOnlyOneStation
                ? "mediumEmphasis"
                : "highEmphasis"
            }
            bg={
              mode === "video" && !hasOnlyOneStation
                ? "white12"
                : hasOnlyOneStation
                ? "transparent"
                : "iconBackground"
            }
            onClick={() =>
              isFreeVersion
                ? purchase.open({ content: "multibaby" })
                : babyStationSelect.open()
            }
            icon={AddStationIcon}
            size={hasOnlyOneStation ? "28px" : "24px"}
            iconSize={hasOnlyOneStation ? "20px" : "12px"}
          />
        </Flex>
      )}
      {renderTilesToggle && (
        <Flex alignItems="center" mt="4px" minWidth="24px">
          <RoundButton
            iconFill={mode === "video" ? "white" : "mediumEmphasis"}
            bg={mode === "video" ? "white12" : "iconBackground"}
            onClick={async () => {
              await onTilesViewToggle();
              multiBabyManager.close();
              toggleTilesView();
            }}
            icon={ToggleTilesIcon}
            size="24px"
            iconSize="12px"
          />
        </Flex>
      )}
    </Flex>
  );
}
